import { downloadFile, useLogging } from '@ecosystem/newron-client-lib';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { CollectionErrors } from '../../shared/constants';
import { getCollection, getCollections, getExportedCollection, getInsight } from '../services/collections.service';
import { Collection, CollectionInsight, CollectionList, CollectionsErrorType } from '../types';

const mapResponseCreatedAtDate = <T>(responseWithDate: T & { createdAt: string }): T & { createdAt: Date } => ({
  ...responseWithDate,
  createdAt: new Date(responseWithDate.createdAt),
});

export function useGetCollections(): {
  collections: CollectionList | null;
  refetch: () => Promise<void>;
  loading: boolean;
  error: CollectionsErrorType;
} {
  const { error: logError } = useLogging();
  const [collections, setCollections] = useState<CollectionList | null>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CollectionsErrorType>(CollectionsErrorType.NO_ERROR);

  const fetchCollections = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      try {
        if (collections && !force) {
          // If collections are already loaded, do not fetch again
          return;
        }
        setLoading(true);

        const collectionsResponse = await getCollections();
        if (!collectionsResponse) {
          throw new Error('Received no data from backend');
        }
        const formatted_collections: CollectionList = {
          items: collectionsResponse.items?.map(mapResponseCreatedAtDate),
          totalCount: collectionsResponse.totalCount,
        };
        setCollections(formatted_collections);
        setError(CollectionsErrorType.NO_ERROR);
      } catch (err) {
        logError(err, { action: 'Fetch collections' });
        setCollections(null);
        setError(CollectionsErrorType.FAILED_TO_GET);
      } finally {
        setLoading(false);
      }
    },
    [collections, logError]
  );

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  return {
    refetch: fetchCollections,
    collections,
    loading,
    error,
  };
}

export function useGetCollection<TInsightData = unknown>(
  collectionId: string
): {
  collection: Collection<TInsightData> | null;
  loading: boolean;
  error?: string;
} {
  const { error: logError } = useLogging();
  const [collection, setCollection] = useState<Collection<TInsightData> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!collectionId) {
      return;
    }

    async function fetchCollection() {
      setLoading(true);
      setError(undefined);
      try {
        const collection = await getCollection<TInsightData>(collectionId);
        setCollection(collection);
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          setError(CollectionErrors.NOT_FOUND_ERROR);
        } else {
          logError(err, { action: 'Fetch collection', collectionId });
          setError(CollectionErrors.LOAD_ERROR);
        }
        setCollection(null);
      } finally {
        setLoading(false);
      }
    }
    fetchCollection();
  }, [logError, collectionId]);

  return {
    collection,
    loading,
    error,
  };
}

export function useGetInsight<TInsightData = unknown>(
  collectionId: string,
  insightId: string
): {
  insight: CollectionInsight<TInsightData> | null;
  loading: boolean;
  error?: string;
} {
  const { error: logError, warning: logWarning } = useLogging();
  const [insight, setInsight] = useState<CollectionInsight<TInsightData> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!collectionId || !insightId) {
      return;
    }

    async function fetchInsight() {
      setLoading(true);
      setError(undefined);
      try {
        const insight = await getInsight<TInsightData>(collectionId, insightId);
        setInsight(insight);
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          logWarning('Error fetching insight', {
            action: 'Fetch insight',
            collectionId,
            insightId,
            message: err.message,
          });
          setError(CollectionErrors.NOT_FOUND_ERROR);
        } else {
          logError(err, { action: 'Fetch insight', collectionId, insightId });
          setError(CollectionErrors.LOAD_ERROR);
        }
        setInsight(null);
      } finally {
        setLoading(false);
      }
    }
    fetchInsight();
  }, [logError, logWarning, collectionId, insightId]);

  return {
    insight,
    loading,
    error,
  };
}

export function useDownloadCollection(): {
  downloadCollection: () => Promise<void>;
  isDownloadCollectionPage: boolean;
  collectionDownloadError: string | null;
} {
  const { error: logError } = useLogging();

  const [isDownloadCollectionPage, setIsDownloadCollectionPage] = useState(false);
  const [collectionDownloadError, setCollectionDownloadError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const exportDownloadId = searchParams.get('download');
  const expiresAt = searchParams.get('expiresAt');
  const decodedExpiresAt = decodeURIComponent(expiresAt);

  const removeSearchParams = useCallback(() => {
    searchParams.delete('download');
    searchParams.delete('expiresAt');
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (exportDownloadId && exportDownloadId !== 'retry') {
      setIsDownloadCollectionPage(true);
    }
    if (exportDownloadId === 'retry') {
      removeSearchParams();
    }
  }, [searchParams, exportDownloadId, removeSearchParams]);

  const downloadCollection = useCallback(async () => {
    const isExpired = new Date(decodedExpiresAt).getTime() < Date.now();

    if (isExpired) {
      setCollectionDownloadError('The export of this collection has expired. Please try again.');
      setIsDownloadCollectionPage(false);
      removeSearchParams();
      return;
    }

    try {
      const file = await getExportedCollection(exportDownloadId);
      downloadFile(file);
    } catch (err) {
      logError(err, { message: `Download collection error: ${err}` });
      setCollectionDownloadError(
        'There has been an error downloading the export of this collection. Please try again.'
      );
    } finally {
      setIsDownloadCollectionPage(false);
      removeSearchParams();
    }
  }, [decodedExpiresAt, exportDownloadId, logError, removeSearchParams]);

  return { isDownloadCollectionPage, downloadCollection, collectionDownloadError };
}
